import { useCallback, useEffect, useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
	AlertTriangle,
	ArrowLeft,
	Check as CheckIcon,
	Download,
	Flag,
	XCircle,
} from "lucide-react";
import { MapContainer, Marker, Polyline, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";

import { getValidationRecordById } from "../config/url";
import { getConfigWithToken } from "../config/config";
import formatPhoneNumber from "../utils/formatPhoneNumber";
import {
	formatCoordinateDegreesLabel,
	formatDurationLabel,
	formatTimeUS,
	formatVisitDate,
	getClientName,
	getClientPhoneNumber,
	getCompletedInterventions,
	getConfirmationStatus,
	getCoordinatePair,
	getFlagCount,
	getGpsAccuracyLabel,
	getGpsAlertMessage,
	getGpsDisplayLabel,
	getGpsDistanceLabel,
	getGpsMissingState,
	getProviderName,
	getVisitStatus,
	isFlaggedVisit,
} from "../utils/visitDisplay";
import { formatValidationFlagLabel, getValidationStatusClass } from "../utils/validationDisplay";
import { getComparisonResultClass, getValidationStatusShortLabel } from "../utils/validationComparison";
import ClientConfirmationPanel from "../components/ClientConfirmationPanel";
import classes from "./ValidationDetails.module.css";

const BRAND = "#064260";

const FitBounds = ({ bounds }) => {
	const map = useMap();
	useEffect(() => {
		if (!bounds || bounds.length === 0) return;
		map.fitBounds(bounds, { padding: [30, 30] });
	}, [bounds, map]);
	return null;
};

const DetailItem = ({ isNode = false, label, value, valueClassName = "" }) => (
	<div className={classes.detailItem}>
		<span className={classes.detailLabel}>{label}</span>
		{isNode ? (
			<div className={classes.detailValueNode}>{value}</div>
		) : (
			<span className={`${classes.detailValue} ${valueClassName}`.trim()}>{value}</span>
		)}
	</div>
);

const formatPhoneValue = (value) => {
	if (!value) return "—";
	try {
		return formatPhoneNumber(String(value)) || value;
	} catch {
		return value;
	}
};

const ValidationDetails = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const currentUser = useSelector((state) => state?.user);
	const token = currentUser?.user?.token || localStorage.getItem("user_id");
	const organizationId =
		currentUser?.user?.organizationId ||
		localStorage.getItem("organizationId") ||
		localStorage.getItem("businessId") ||
		"";

	const [record, setRecord] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [flaggedForReview, setFlaggedForReview] = useState(false);

	const requestConfig = useMemo(
		() => ({
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
				...(token ? getConfigWithToken(token).headers : {}),
				...(organizationId ? { "x-organization-id": organizationId } : {}),
			},
		}),
		[organizationId, token],
	);

	const loadRecord = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const response = await axios.get(getValidationRecordById(id), {
				...requestConfig,
				params: organizationId ? { organizationId } : undefined,
			});
			if (response.data?.success === false) {
				throw new Error(response.data?.message || "Validation record not found.");
			}
			setRecord(response?.data?.data || null);
		} catch (err) {
			setRecord(null);
			setError(
				err?.response?.data?.message ||
					err?.message ||
					"Unable to load validation details. Please try again.",
			);
		} finally {
			setLoading(false);
		}
	}, [id, organizationId, requestConfig]);

	useEffect(() => {
		loadRecord();
	}, [loadRecord]);

	const visit = record?.teletraceVisit || null;
	const hasTeletraceVisit = Boolean(record?.hasTeletraceVisit && visit);
	const comparisonRows = record?.comparisonRows || [];
	const detailFlags = record?.detailFlags || [];
	const missingAlert = record?.missingAlert || null;

	const mapIcons = useMemo(
		() => ({
			end: L.divIcon({
				className: classes.markerWrap,
				html: `<div class="${classes.labeledMarker} ${classes.labeledMarkerEnd}"><span class="${classes.labeledMarkerDot}"></span><span class="${classes.labeledMarkerText}">End</span></div>`,
				iconAnchor: [24, 42],
				iconSize: [48, 48],
			}),
			start: L.divIcon({
				className: classes.markerWrap,
				html: `<div class="${classes.labeledMarker} ${classes.labeledMarkerStart}"><span class="${classes.labeledMarkerDot}"></span><span class="${classes.labeledMarkerText}">Start</span></div>`,
				iconAnchor: [24, 42],
				iconSize: [48, 48],
			}),
		}),
		[],
	);

	const visitStatus = visit ? getVisitStatus(visit) : null;
	const flagged = visit ? isFlaggedVisit(visit) : false;
	const confirmationStatus = visit ? getConfirmationStatus(visit) || "—" : "—";
	const gpsDisplayLabel = visit ? getGpsDisplayLabel(visit) : "—";
	const interventions = visit ? getCompletedInterventions(visit) : [];
	const flagCount = visit ? getFlagCount(visit) : record?.flagCount || 0;
	const { start, end } = visit ? getCoordinatePair(visit.location) : { end: null, start: null };
	const gpsMissing = visit ? getGpsMissingState(visit) : { hasEnd: false, hasStart: false };
	const gpsAlertMessage = visit ? getGpsAlertMessage(visit) : null;
	const hasStart = gpsMissing.hasStart;
	const hasEnd = gpsMissing.hasEnd;
	const showMap = hasStart || hasEnd;
	const mapBounds = [
		...(hasStart ? [[start.lat, start.lng]] : []),
		...(hasEnd ? [[end.lat, end.lng]] : []),
	];

	const successValueClass = classes.detailValueSuccess;
	const warningValueClass = classes.detailValueWarning;
	const dangerValueClass = classes.detailValueDanger;
	const mutedValueClass = classes.detailValueMuted;

	const getStatusValueClass = (value) => {
		if (value === "Verified" || value === "Valid" || value === "Confirmed") return successValueClass;
		if (value === "Missing") return dangerValueClass;
		if (value === "Flagged" || value === "Invalid" || value === "Unconfirmed") return warningValueClass;
		return mutedValueClass;
	};

	const renderVisitStatusBadge = (status) => {
		if (!status || status === "—") return "—";
		const badgeClass =
			status === "Verified"
				? classes.statusVerified
				: status === "Flagged"
					? classes.statusFlagged
					: classes.statusIncomplete;
		return (
			<span className={badgeClass}>
				<span className={classes.statusDot} />
				{status}
			</span>
		);
	};

	const renderValidationStatusBadge = (status) => {
		const statusClass = getValidationStatusClass(status);
		return (
			<span className={classes[statusClass]}>
				<span aria-hidden="true" className={classes.statusDot} />
				{getValidationStatusShortLabel(status)}
			</span>
		);
	};

	const renderComparisonValue = (value, tone) => (
		<span className={tone === "warning" ? classes.comparisonValueWarning : classes.comparisonValue}>
			{value}
		</span>
	);

	const handleExport = () => {
		toast.info("Validation export will be available soon.");
	};

	const handleFlagForReview = () => {
		setFlaggedForReview((current) => {
			const next = !current;
			toast.success(next ? "Record flagged for review." : "Review flag removed.");
			return next;
		});
	};

	if (loading) {
		return (
			<div className={classes.stateCard}>
				<Spinner animation="border" style={{ color: BRAND }} />
				<p className={classes.stateText}>Loading validation details...</p>
			</div>
		);
	}

	if (error || !record) {
		return (
			<div className={classes.stateCard}>
				<p className={classes.stateText}>{error || "Validation record not found."}</p>
				<button className={classes.outlineButton} onClick={() => navigate("/validate")} type="button">
					Back to Validate
				</button>
			</div>
		);
	}

	const ehrColumnLabel = record.ehrVisitId || "EHR";
	const teletraceColumnLabel = record.teletraceVisitId || "—";

	return (
		<div className={classes.page}>
			<div className={classes.pageTop}>
				<button
					aria-label="Back to Validate"
					className={classes.backButton}
					onClick={() => navigate("/validate")}
					type="button">
					<ArrowLeft color="#ffffff" size={18} strokeWidth={2.25} />
				</button>
				<div>
					<h1 className={classes.pageTitle}>Validation Details</h1>
					<p className={classes.pageMeta}>
						{record.clientName} · {formatVisitDate(record.dateOfService)}
					</p>
				</div>
			</div>

			<div className={classes.headerCard}>
				<div className={classes.headerMain}>
					<div className={classes.headerIdBlock}>
						<div className={classes.headerIdLabel}>EHR Visit ID</div>
						<div className={classes.headerIdValue}>{record.ehrVisitId || "—"}</div>
					</div>
					<div aria-hidden="true" className={classes.headerDivider} />
					<div className={classes.headerIdBlock}>
						<div className={classes.headerIdLabel}>TeleTrace Visit ID</div>
						<div className={classes.headerIdValue}>{record.teletraceVisitId || "—"}</div>
					</div>
					<div aria-hidden="true" className={classes.headerDivider} />
					<div className={classes.headerBadges}>
						{renderValidationStatusBadge(record.validationStatus)}
						{flagCount > 0 ? (
							<span className={classes.flagCountBadge}>
								<span className={classes.statusDot} />
								{formatValidationFlagLabel(flagCount)}
							</span>
						) : null}
					</div>
				</div>
				<div className={classes.headerActions}>
					<button
						className={flaggedForReview ? classes.reviewButtonActive : classes.reviewButton}
						onClick={handleFlagForReview}
						type="button">
						<Flag size={15} strokeWidth={2} />
						{flaggedForReview ? "Flagged for Review" : "Flag for Review"}
					</button>
					<button className={classes.outlineButton} onClick={handleExport} type="button">
						<Download size={15} strokeWidth={2} />
						Export Results
					</button>
				</div>
			</div>

			{missingAlert ? (
				<div className={classes.alertBanner} role="alert">
					<div className={classes.alertTitle}>{missingAlert.title}</div>
					<p className={classes.alertMessage}>{missingAlert.message}</p>
					{missingAlert.footerMessage ? (
						<p className={classes.alertFooter}>{missingAlert.footerMessage}</p>
					) : null}
				</div>
			) : null}

			{record.recommendedAction ? (
				<section className={classes.actionSection}>
					<h2 className={classes.flagsTitle}>Recommended Action</h2>
					<p className={classes.actionText}>{record.recommendedAction}</p>
				</section>
			) : null}

			{detailFlags.length > 0 ? (
				<section className={classes.flagsSection}>
					<h2 className={classes.flagsTitle}>Flags</h2>
					<ul className={classes.flagsList}>
						{detailFlags.map((flag) => (
							<li className={classes.flagItem} key={flag}>
								<AlertTriangle className={classes.flagIcon} size={15} strokeWidth={2.25} />
								<span>{flag}</span>
							</li>
						))}
					</ul>
				</section>
			) : null}

			<div className={classes.sectionStack}>
				<section className={classes.sectionCard}>
					<div className={classes.comparisonTableWrap}>
						<table className={classes.comparisonTable}>
							<thead>
								<tr>
									<th>Field</th>
									<th>EHR — {ehrColumnLabel}</th>
									<th>TeleTrace — {teletraceColumnLabel}</th>
									<th>Result</th>
								</tr>
							</thead>
							<tbody>
								{comparisonRows.map((row) => (
									<tr key={row.field}>
										<td className={classes.comparisonField}>{row.field}</td>
										<td>{renderComparisonValue(row.ehrValue, row.valueTone)}</td>
										<td>{renderComparisonValue(row.teletraceValue, row.valueTone)}</td>
										<td>
											<span className={classes[getComparisonResultClass(row.result)]}>
												{row.result}
											</span>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</section>

				{hasTeletraceVisit ? (
					<>
						<section className={classes.sectionCard}>
							<h2 className={classes.sectionTitle}>Visit Details</h2>
							<div className={classes.detailsGrid}>
								<DetailItem label="Client" value={getClientName(visit) || record.clientName} />
								<DetailItem label="Provider" value={getProviderName(visit) || record.providerName} />
								<DetailItem label="Date" value={formatVisitDate(visit.date || record.dateOfService)} />
								<DetailItem label="Start" value={formatTimeUS(visit.time_in)} />
								<DetailItem label="End" value={formatTimeUS(visit.time_out)} />
								<DetailItem label="Duration" value={formatDurationLabel(visit)} />
								<DetailItem isNode label="Status" value={renderVisitStatusBadge(visitStatus)} />
								<DetailItem
									label="GPS"
									value={gpsDisplayLabel}
									valueClassName={getStatusValueClass(gpsDisplayLabel)}
								/>
								<DetailItem
									label="Confirmation"
									value={confirmationStatus}
									valueClassName={getStatusValueClass(confirmationStatus)}
								/>
							</div>
						</section>

						<section className={classes.sectionCard}>
							<h2 className={classes.sectionTitle}>Client Confirmation</h2>
							<ClientConfirmationPanel
								flagged={flagged}
								phoneNumber={formatPhoneValue(getClientPhoneNumber(visit))}
								styles={classes}
								verifiedAtFallback={
									flagged ? visit?.time_out || visit?.updated_at : visit?.time_out
								}
								visit={visit}
							/>
						</section>

						<section className={classes.sectionCard}>
							<h2 className={classes.sectionTitle}>GPS Information</h2>
							{gpsAlertMessage ? (
								<div
									className={`${classes.gpsAlert} ${showMap ? classes.gpsPartialAlert : ""}`.trim()}
									role="alert">
									<XCircle className={classes.gpsAlertIcon} size={18} strokeWidth={2.25} />
									<span>{gpsAlertMessage}</span>
								</div>
							) : null}

							{showMap ? (
								<div className={classes.mapContainer}>
									<MapContainer
										center={[hasStart ? start.lat : end.lat, hasStart ? start.lng : end.lng]}
										scrollWheelZoom={false}
										style={{ width: "100%", height: 340 }}
										zoom={13}>
										<TileLayer
											attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
											url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
										/>
										<FitBounds bounds={mapBounds} />
										{hasStart ? (
											<Marker icon={mapIcons.start} position={[start.lat, start.lng]} />
										) : null}
										{hasEnd ? <Marker icon={mapIcons.end} position={[end.lat, end.lng]} /> : null}
										{hasStart && hasEnd ? (
											<Polyline
												pathOptions={{ color: "#27b99a", opacity: 0.7, weight: 3 }}
												positions={[
													[start.lat, start.lng],
													[end.lat, end.lng],
												]}
											/>
										) : null}
									</MapContainer>
								</div>
							) : null}

							{showMap ? (
								<div className={classes.gpsMetaGrid}>
									<DetailItem
										label="Start Coords"
										value={formatCoordinateDegreesLabel(visit, "start")}
									/>
									<DetailItem label="End Coords" value={formatCoordinateDegreesLabel(visit, "end")} />
									<DetailItem label="Accuracy" value={getGpsAccuracyLabel(visit)} />
									<DetailItem
										label="Distance"
										value={getGpsDistanceLabel(visit)}
										valueClassName={getStatusValueClass(gpsDisplayLabel)}
									/>
								</div>
							) : null}
						</section>

						<section className={classes.sectionCard}>
							<h2 className={classes.sectionTitle}>Completed Interventions</h2>
							{interventions.length ? (
								<div className={classes.interventionList}>
									{interventions.map((item, index) => (
										<div className={classes.interventionTag} key={`${item}-${index}`}>
											<span className={classes.interventionIconWrap}>
												<CheckIcon size={12} strokeWidth={3} />
											</span>
											{item}
										</div>
									))}
								</div>
							) : (
								<div className={classes.emptyState}>No completed interventions recorded.</div>
							)}
						</section>
					</>
				) : null}

				{missingAlert ? (
					<div className={classes.alertBannerDanger} role="alert">
						<div className={classes.alertTitle}>{missingAlert.title}</div>
						<p className={classes.alertMessage}>{missingAlert.footerMessage}</p>
					</div>
				) : null}
			</div>
		</div>
	);
};

export default ValidationDetails;
